import { useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useFetch } from "@/lib/useFetch";
import { useAuth } from "@/context/AuthContext";
import PlanGate from "@/app/PlanGate";
import { PageHeader, Btn, EmptyState, Spinner, Money } from "@/app/ui";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Boxes, Plus, Minus, Trash2 } from "lucide-react";

function MaterialsInner() {
  const { user } = useAuth();
  const sym = user?.business?.currency || "£";
  const { data, loading, refetch } = useFetch("/materials");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", sku: "", unit_cost: 0, sell_price: 0, stock: 0, unit: "each" });

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const save = async () => {
    if (!form.name.trim()) return toast.error("Name required");
    try {
      await api.post("/materials", { ...form, unit_cost: Number(form.unit_cost), sell_price: Number(form.sell_price), stock: Number(form.stock) });
      toast.success("Material added"); setOpen(false); refetch();
      setForm({ name: "", sku: "", unit_cost: 0, sell_price: 0, stock: 0, unit: "each" });
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const adjust = async (id, delta) => { await api.post(`/materials/${id}/adjust`, { delta, reason: delta > 0 ? "restock" : "used" }); refetch(); };
  const remove = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/materials/${id}`); refetch(); };

  const items = data?.items || [];
  return (
    <div className="animate-fade-up">
      <PageHeader title="Materials Register" description="Track stock, costs and margins."
        action={<Btn variant="accent" onClick={() => setOpen(true)} data-testid="create-material-button"><Plus size={16} /> New material</Btn>} />
      {loading ? <Spinner /> : items.length === 0 ? (
        <EmptyState icon={Boxes} title="No materials yet" description="Add stock items to track quantities and margins."
          action={<Btn variant="accent" onClick={() => setOpen(true)}><Plus size={16} /> New material</Btn>} />
      ) : (
        <div className="amden-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-left"><tr>
              <th className="px-4 py-3 font-semibold">Material</th><th className="px-4 py-3 font-semibold text-right hidden sm:table-cell">Cost</th>
              <th className="px-4 py-3 font-semibold text-right hidden sm:table-cell">Sell</th><th className="px-4 py-3 font-semibold text-right">Margin</th>
              <th className="px-4 py-3 font-semibold text-center">Stock</th><th></th></tr></thead>
            <tbody>
              {items.map((m) => {
                const margin = m.sell_price - m.unit_cost;
                const marginPct = m.sell_price ? Math.round((margin / m.sell_price) * 100) : 0;
                return (
                  <tr key={m.id} className="border-t border-slate-100 hover:bg-slate-50/80" data-testid={`material-row-${m.id}`}>
                    <td className="px-4 py-3"><p className="font-medium text-navy">{m.name}</p><p className="text-xs text-slate-400 font-mono">{m.sku}</p></td>
                    <td className="px-4 py-3 text-right hidden sm:table-cell"><Money value={m.unit_cost} symbol={sym} /></td>
                    <td className="px-4 py-3 text-right hidden sm:table-cell"><Money value={m.sell_price} symbol={sym} /></td>
                    <td className="px-4 py-3 text-right"><Money value={margin} symbol={sym} className="text-emerald-600" /> <span className="text-xs text-slate-400">({marginPct}%)</span></td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-2">
                        <button onClick={() => adjust(m.id, -1)} className="h-6 w-6 grid place-items-center rounded border border-slate-300 text-slate-500 hover:bg-slate-100"><Minus size={13} /></button>
                        <span className="font-mono w-10 text-center">{m.stock}{m.unit === "m" ? "m" : ""}</span>
                        <button onClick={() => adjust(m.id, 1)} data-testid={`material-add-${m.id}`} className="h-6 w-6 grid place-items-center rounded border border-slate-300 text-slate-500 hover:bg-slate-100"><Plus size={13} /></button>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right"><button onClick={() => remove(m.id)} className="text-slate-300 hover:text-red-600"><Trash2 size={15} /></button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>New material</DialogTitle></DialogHeader>
          <div className="space-y-3 pt-2">
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-sm font-medium text-slate-700">Name *</label><input value={form.name} onChange={set("name")} data-testid="material-name" className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2" /></div>
              <div><label className="text-sm font-medium text-slate-700">SKU</label><input value={form.sku} onChange={set("sku")} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2" /></div>
              <div><label className="text-sm font-medium text-slate-700">Unit cost</label><input type="number" value={form.unit_cost} onChange={set("unit_cost")} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 font-mono" /></div>
              <div><label className="text-sm font-medium text-slate-700">Sell price</label><input type="number" value={form.sell_price} onChange={set("sell_price")} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 font-mono" /></div>
              <div><label className="text-sm font-medium text-slate-700">Stock</label><input type="number" value={form.stock} onChange={set("stock")} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 font-mono" /></div>
              <div><label className="text-sm font-medium text-slate-700">Unit</label><input value={form.unit} onChange={set("unit")} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2" /></div>
            </div>
          </div>
          <DialogFooter><Btn variant="secondary" onClick={() => setOpen(false)}>Cancel</Btn><Btn variant="accent" onClick={save} data-testid="material-save-button">Save</Btn></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function Materials() { return <PlanGate feature="materials"><MaterialsInner /></PlanGate>; }
